import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { pingState, type PingState } from '../notify.js';
import { PageHead, toast } from '../ui.js';
import { CalendarFeed } from './Crew.js';
import type { HiddenProject } from './hideProject.js';
import { MfaStep } from './PasswordSignin.js';
import { loadSession, saveSession, sessionFrom } from '../auth.js';

/**
 * "Mina inställningar" (owner 2026-10-20: every role has a settings page in the menu): what each person can change for
 * themselves. Admins find it as the first tab next to the Hub's own settings.
 */

const TX = texts(
  {
    title: 'My settings',
    sub: 'What you can change for yourself. Nobody else is affected.',
    language: 'Language',
    languageInfo: 'The Hub’s menus and the emails it sends you.',
    alerts: 'Alerts on this computer',
    alertsInfo: 'A small alert when a meeting is about to start or the timer asks if you are still working.',
    alertsOn: 'On',
    alertsTurnOn: 'Turn on',
    alertsBlocked: 'Blocked in the browser. Allow notifications for this site in the browser’s settings.',
    calendar: 'Calendar on your phone',
    popup: 'News when you open the Hub',
    popupInfo: 'A box with what is new since you were last here: news from KFF and new things in the Hub.',
    on: 'On',
    off: 'Off',
    hidden: 'Hidden projects',
    hiddenInfo: 'Projects you hid from My work. Only you are affected.',
    noHidden: 'No hidden projects. Use Hide on a row in My work.',
    show: 'Show again',
    shown: (name: string) => `${name} is back in My work`,
    signin: 'Sign-in',
    signinInfo: (email: string) => `Your username is ${email}.`,
    via: { google: 'You signed in with Google.', password: 'You signed in with your password.', link: 'You signed in with your personal link or an email code.' } as Record<string, string>,
    password: 'Password',
    passwordSet: (d: string) => `Chosen ${d}.`,
    passwordNone: 'No password yet.',
    passwordLink: 'Email me a link to choose a password',
    passwordSent: (email: string) => `A link is on its way to ${email}. It works for one hour.`,
    mfa: 'Two-step verification',
    mfaInfo: 'A code from an authenticator app on your phone as well as the password.',
    mfaOn: 'On',
    mfaOffState: 'Off',
    mfaRequired: 'Required for you.',
    mfaTurnOn: 'Turn on',
    mfaTurnOff: 'Turn off',
    mfaDone: 'Two-step verification is on',
    mfaRemoved: 'Two-step verification is off',
    mfaLink: 'Sign in with your password to set up two-step verification.',
    cancel: 'Cancel'
  },
  {
    title: 'Mina inställningar',
    sub: 'Det du kan ändra för dig själv. Ingen annan påverkas.',
    language: 'Språk',
    languageInfo: 'Hubbens menyer och mejlen den skickar till dig.',
    alerts: 'Aviseringar på den här datorn',
    alertsInfo: 'En liten avisering när ett möte snart börjar eller när timern frågar om du fortfarande jobbar.',
    alertsOn: 'På',
    alertsTurnOn: 'Slå på',
    alertsBlocked: 'Blockerade i webbläsaren. Tillåt aviseringar för den här sidan i webbläsarens inställningar.',
    calendar: 'Kalender i mobilen',
    popup: 'Nyheter när du öppnar Hubben',
    popupInfo: 'En ruta med det som är nytt sedan du var här sist: nyheter från KFF och nytt i Hubben.',
    on: 'På',
    off: 'Av',
    hidden: 'Dolda projekt',
    hiddenInfo: 'Projekt du har dolt från Mitt arbete. Bara du påverkas.',
    noHidden: 'Inga dolda projekt. Använd Dölj på en rad i Mitt arbete.',
    show: 'Visa igen',
    shown: (name: string) => `${name} syns i Mitt arbete igen`,
    signin: 'Inloggning',
    signinInfo: (email: string) => `Ditt användarnamn är ${email}.`,
    via: { google: 'Du loggade in med Google.', password: 'Du loggade in med ditt lösenord.', link: 'Du loggade in med din personliga länk eller en kod till mejlen.' } as Record<string, string>,
    password: 'Lösenord',
    passwordSet: (d: string) => `Valt ${d}.`,
    passwordNone: 'Inget lösenord än.',
    passwordLink: 'Mejla mig en länk för att välja lösenord',
    passwordSent: (email: string) => `En länk är på väg till ${email}. Den gäller i en timme.`,
    mfa: 'Tvåstegsverifiering',
    mfaInfo: 'En kod från en autentiseringsapp i mobilen utöver lösenordet.',
    mfaOn: 'På',
    mfaOffState: 'Av',
    mfaRequired: 'Krävs för dig.',
    mfaTurnOn: 'Slå på',
    mfaTurnOff: 'Stäng av',
    mfaDone: 'Tvåstegsverifiering är på',
    mfaRemoved: 'Tvåstegsverifiering är av',
    mfaLink: 'Logga in med ditt lösenord för att slå på tvåstegsverifiering.',
    cancel: 'Avbryt'
  }
);

interface Security {
  signIn: 'google' | 'password' | 'link';
  email: string;
  password: { available: boolean; setAt: string | null };
  mfa: { enrolled: boolean; required: boolean };
}

/** Sign-in (owner 2026-10-28): the password (a link to choose one) and two-step verification for yourself. */
function SignInSection(p: { api: Api }) {
  const lang = useLang();
  const t = TX[lang];
  const [sec, setSec] = useState<Security | null>(null);
  const [rev, setRev] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  useEffect(() => {
    p.api.get<Security>('/api/me/security').then(setSec, () => setSec(null));
  }, [p.api, rev]);
  if (!sec) return null;
  const say = (e: unknown) => toast({ text: e instanceof Error ? e.message : String(e) });
  const session = loadSession();
  const day = (iso: string) => new Date(iso).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const link = async () => {
    try {
      const r = await p.api.post<{ sentTo: string }>('/api/me/password-link', {});
      toast({ text: t.passwordSent(r.sentTo) });
    } catch (e) {
      say(e);
    }
  };
  const off = async () => {
    try {
      await p.api.post('/api/auth/mfa/remove', { lang });
      toast({ text: t.mfaRemoved });
      setRev((n) => n + 1);
    } catch (e) {
      say(e);
    }
  };
  return (
    <section class="panel ms-sec" aria-labelledby="ms-signin">
      <h2 class="blk-title" id="ms-signin">
        {t.signin}
      </h2>
      <p class="small muted">
        {t.signinInfo(sec.email)} {t.via[sec.signIn]}
      </p>
      {sec.password.available ? (
        <>
          <h3 class="ms-sub">{t.password}</h3>
          <div class="sec-row">
            <span class="small">{sec.password.setAt ? t.passwordSet(day(sec.password.setAt)) : t.passwordNone}</span>
            <button type="button" class="btn btn-sm" onClick={() => void link()}>
              {t.passwordLink}
            </button>
          </div>
          <h3 class="ms-sub">{t.mfa}</h3>
          <p class="small muted">{t.mfaInfo}</p>
          {enrolling && session ? (
            <MfaStep
              token={session.accessToken}
              mode="enroll"
              onCancel={() => setEnrolling(false)}
              onDone={(s) => {
                // the new session has passed two-step: kept, so the next request sends it
                saveSession(sessionFrom(s));
                setEnrolling(false);
                toast({ text: t.mfaDone });
                setRev((n) => n + 1);
              }}
            />
          ) : (
            <div class="sec-row">
              <span class="small">
                <b>{sec.mfa.enrolled ? t.mfaOn : t.mfaOffState}</b>
                {sec.mfa.required ? ` · ${t.mfaRequired}` : ''}
              </span>
              {sec.signIn === 'link' ? (
                !sec.mfa.enrolled ? <span class="small muted">{t.mfaLink}</span> : null
              ) : sec.mfa.enrolled ? (
                !sec.mfa.required ? (
                  <button type="button" class="btn btn-sm" onClick={() => void off()}>
                    {t.mfaTurnOff}
                  </button>
                ) : null
              ) : (
                <button type="button" class="btn btn-sm" onClick={() => setEnrolling(true)}>
                  {t.mfaTurnOn}
                </button>
              )}
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

const LANGS: [Lang, string][] = [
  ['sv', 'Svenska'],
  ['en', 'English']
];

/** `newsPopup`: the start-up news box is on (owner 2026-10-21: on by default, each person chooses); undefined hides the choice. */
export function MySettings(p: { api: Api; onLang?: (l: Lang) => void; calendar: boolean; newsPopup?: boolean }) {
  const lang = useLang();
  const t = TX[lang];
  const [ping, setPing] = useState<PingState>(pingState);
  const [hidden, setHidden] = useState<HiddenProject[] | null>(null);
  const [rev, setRev] = useState(0);
  const [popup, setPopup] = useState(p.newsPopup);
  useEffect(() => {
    p.api.get<{ hidden: HiddenProject[] }>('/api/me/hidden-projects').then(
      (r) => setHidden(r.hidden),
      () => setHidden([])
    );
  }, [p.api, rev]);
  const allow = async () => {
    try {
      await Notification.requestPermission();
    } catch {
      /* stays as it was */
    }
    setPing(pingState());
  };
  const choosePopup = async (on: boolean) => {
    const was = popup;
    setPopup(on);
    try {
      await p.api.put('/api/me/news-popup', { on });
    } catch (e) {
      setPopup(was);
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  const show = async (h: HiddenProject) => {
    try {
      await p.api.put(`/api/me/hidden-projects/${h.projectId}`, { hidden: false });
      setRev((n) => n + 1);
      toast({ text: t.shown(h.projectName) });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <div class="screen my-settings">
      <PageHead title={t.title} sub={t.sub} />
      {p.onLang ? (
        <section class="panel ms-sec" aria-labelledby="ms-lang">
          <h2 class="blk-title" id="ms-lang">
            {t.language}
          </h2>
          <p class="small muted">{t.languageInfo}</p>
          <div class="seg" role="group" aria-label={t.language}>
            {LANGS.map(([k, label]) => (
              <button type="button" class={`seg-btn${lang === k ? ' is-on' : ''}`} aria-pressed={lang === k} onClick={() => p.onLang!(k)}>
                {label}
              </button>
            ))}
          </div>
        </section>
      ) : null}
      {ping !== 'none' ? (
        <section class="panel ms-sec" aria-labelledby="ms-alerts">
          <h2 class="blk-title" id="ms-alerts">
            {t.alerts}
          </h2>
          <p class="small muted">{t.alertsInfo}</p>
          {ping === 'on' ? <p class="ms-on">{t.alertsOn}</p> : ping === 'blocked' ? <p class="small">{t.alertsBlocked}</p> : (
            <button type="button" class="btn" onClick={() => void allow()}>
              {t.alertsTurnOn}
            </button>
          )}
        </section>
      ) : null}
      {popup !== undefined ? (
        <section class="panel ms-sec" aria-labelledby="ms-popup">
          <h2 class="blk-title" id="ms-popup">
            {t.popup}
          </h2>
          <p class="small muted">{t.popupInfo}</p>
          <div class="seg" role="group" aria-label={t.popup}>
            {([true, false] as const).map((on) => (
              <button type="button" class={`seg-btn${popup === on ? ' is-on' : ''}`} aria-pressed={popup === on} onClick={() => void choosePopup(on)}>
                {on ? t.on : t.off}
              </button>
            ))}
          </div>
        </section>
      ) : null}
      {p.calendar ? (
        <section class="panel ms-sec" aria-labelledby="ms-cal">
          <h2 class="blk-title" id="ms-cal">
            {t.calendar}
          </h2>
          <CalendarFeed api={p.api} />
        </section>
      ) : null}
      <SignInSection api={p.api} />
      <section class="panel ms-sec" aria-labelledby="ms-hidden">
        <h2 class="blk-title" id="ms-hidden">
          {t.hidden}
        </h2>
        <p class="small muted">{t.hiddenInfo}</p>
        {hidden === null ? null : hidden.length ? (
          <ul class="td-list">
            {hidden.map((h) => (
              <li key={h.projectId} class="td-row">
                <span class="td-what">
                  <span>{h.projectName}</span>
                  <span class="small muted td-sub">{h.client}</span>
                </span>
                <span class="mw-act">
                  <button type="button" class="btn btn-sm" onClick={() => void show(h)}>
                    {t.show}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p class="small">{t.noHidden}</p>
        )}
      </section>
    </div>
  );
}
